import React, {
  useEffect, useState, useRef, Fragment,
} from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { FieldUpdateHandlerParam } from 'shared/form';
import { FormLayoutContent, FormLayoutFooter, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import UtimacoPacketSwitchForm from 'routes/UtimacoPacketSwitch/components/UtimacoPacketSwitchForm';
import { Ref } from 'routes/UtimacoPacketSwitch/components/UtimacoPacketSwitchForm/UtimacoPacketSwitchForm';
import { RootState } from 'data/root.reducers';
import * as selectors from 'data/utimacoPacketSwitch/utimacoPacketSwitch.selectors';
import * as actions from 'data/utimacoPacketSwitch/utimacoPacketSwitch.actions';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as accessFunctionSelectors from 'data/accessFunction/accessFunction.selectors';
import * as collectionFunctionSelectors from 'data/collectionFunction/collectionFunction.selectors';
import * as collectionFunctionGroupSelectors from 'data/collectionFunctionGroup/collectionFunctionGroup.selectors';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import { UtimacoPacketSwitch } from 'data/utimacoPacketSwitch/utimacoPacketSwitch.types';
import { utimacoPacketSwitchTemplate } from '../../templates';
import { adaptTemplateVisibilityMode, adaptTemplateOptions } from '../../utils/UtimacoPacketSwitch.adapter';
import { getAuditDetailsToSubmit } from '../../utils/utimacoPacketSwitchDataToSubmit';

type RouterParams = {
  history: string
}
const mapState = (state: RootState) => ({
  utimacoPacketSwitchTemplateDef: adaptTemplateVisibilityMode(utimacoPacketSwitchTemplate, 'edit'),
  deliveryFunctionId: state.global.selectedDF,
  userId: state.authentication.userId,
  userName: authenticationSelectors.getAuthenticationUserName(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  accessFunctions: accessFunctionSelectors.getAccessFunctions(state),
  collectionFunctions: collectionFunctionSelectors.getCollectionFunctionList(state),
  collectionFunctionGroups: collectionFunctionGroupSelectors.getCollectionFunctionGroupList(state),
  selectedUtimacoPacketSwitch: selectors.getSelectedUtimacoPacketSwitch(state)[0],
  utimacoPacketSwitch: selectors.getUtimacoPacketSwitch(state),
  originalSelectedUtimacoPacketSwitch: selectors.getSelectedUtimacoPacketSwitch(state)[0],
});

const mapDispatch = {
  updateUtimacoPacketSwitch: actions.updateUtimacoPacketSwitch,
  goToUtimacoPacketSwitchList: actions.goToUtimacoPacketSwitchList,
  getUtimacoPacketSwitch: actions.getUtimacoPacketSwitch,
};
const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

export const UtimacoPacketSwitchEdit = ({
  history,
  selectedUtimacoPacketSwitch,
  originalSelectedUtimacoPacketSwitch,
  utimacoPacketSwitch,
  userId,
  deliveryFunctionId,
  userName,
  isAuditEnabled,
  utimacoPacketSwitchTemplateDef,
  accessFunctions,
  collectionFunctions,
  collectionFunctionGroups,
  updateUtimacoPacketSwitch,
  goToUtimacoPacketSwitchList,
  getUtimacoPacketSwitch,
}: Props): JSX.Element => {

  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<Partial<UtimacoPacketSwitch>>({});
  const [template, setTemplate] = useState(utimacoPacketSwitchTemplateDef);
  const { t } = useTranslation();

  const id = selectedUtimacoPacketSwitch?.id;

  useEffect(() => {
    if (id) {
      getUtimacoPacketSwitch(deliveryFunctionId, id);
    }
  }, [deliveryFunctionId, id, getUtimacoPacketSwitch]);

  useEffect(() => {
    if (utimacoPacketSwitch != null && template != null) {
      const clonedTemplate = cloneDeep(template);
      setFormData(utimacoPacketSwitch);
      clonedTemplate.formId = Date.now().toString();
      clonedTemplate.key = Date.now().toString();
      setTemplate(clonedTemplate);
    }
  }, [
    id,
    utimacoPacketSwitch,
  ]);

  useEffect(() => {
    setTemplate(
      adaptTemplateOptions(
        template,
        accessFunctions,
        collectionFunctions,
        collectionFunctionGroups,
        formData,
      ),
    );
  }, [
    formData,
    accessFunctions,
    collectionFunctions,
    collectionFunctionGroups,
  ]);

  const getCreateDistributorActionPayload = () => {
    if (formRef.current === undefined || formRef.current === null) return null;

    if (!formRef.current.isValid(true, true)) {
      return null;
    }

    const utimacoPacketSwitchData = formRef.current.getValues();
    const payload = {
      data: utimacoPacketSwitchData,
      auditDetails: getAuditDetailsToSubmit(
        utimacoPacketSwitchData,
        originalSelectedUtimacoPacketSwitch,
        isAuditEnabled,
        userId,
        userName,
        'UPDATE',
      ),
    };

    return payload;

  };

  const handleClickSave = () => {
    const payload = getCreateDistributorActionPayload();
    if (payload) {
      updateUtimacoPacketSwitch(deliveryFunctionId, selectedUtimacoPacketSwitch?.id, payload, history);
    }
  };

  const handleCancel = () => goToUtimacoPacketSwitchList(history);

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    if (formData != null) {
      return;
    }

    const clonedFormData = cloneDeep(formData as UtimacoPacketSwitch);

    setFormData({
      ...clonedFormData,
      [fieldName]: value,
    });
  };

  const renderForm = () => {
    if (formData && template) {
      return (
        <UtimacoPacketSwitchForm
          ref={formRef}
          data={formData}
          template={template}
          isEditing
          onFieldUpdate={handleFieldUpdate}
        />
      );
    }
  };
  const renderFormPanel = (formTitle:string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing
          onClickCancel={handleCancel}
          onClickSave={handleClickSave}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  if (utimacoPacketSwitch == null) {
    return <Fragment />;
  }

  return (
    <div data-testid="UtimacoPacketSwitchEdit">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel(t('utimacoPacketSwitch.edit.title'))}
          {renderForm()}
        </FormLayoutContent>
        <FormLayoutFooter>
          {renderFormPanel('')}
        </FormLayoutFooter>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(UtimacoPacketSwitchEdit));
